import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useCallback, useMemo } from "react";
import type { CubeWindow } from "../../components/cubeDataTable/utils/columnUtils";
import type {
  ForecastVersionSelections,
  SaveCubeKey
} from "../../components/forecastContextSelector/ForecastContextSelector";
import { isAlgorithmicProducer } from "../../utils/algorithmicProducer";
import { useFetchVersions } from "../useFetchVersions";
import type { CubeProducer } from "../useForecastWorkspace";
import { useScopedBudgetVersions } from "../useScopedBudgetVersions";

/** Options for the version / cube selection sub-hook. */
interface UseMainPageVersionsOptions {
  /** Which producer this page operates on ("user" for forecast, "budget" for budget). */
  producer: CubeProducer;
  /**
   * Which version key holds the writable scalar cube id (user or
   * budget). Narrowed from ``keyof ForecastVersionSelections`` so
   * ``selectedVersionOptions[saveCubeKey]`` resolves directly to
   * ``string | undefined`` without a cast.
   */
  saveCubeKey: SaveCubeKey;
  /** Current context filter selections (used to derive selectedBrand). */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Current version selections from the UI. */
  selectedVersionOptions: ForecastVersionSelections;
}

/**
 * Manages version fetching, frozen state, cube ID derivation, and
 * cube-ID resolution for the main page.
 */
export function useMainPageVersions({
  producer,
  saveCubeKey,
  selectedContextOptions,
  selectedVersionOptions
}: UseMainPageVersionsOptions) {
  const { data: versionsData } = useFetchVersions();
  // A brand-scoped budget cube is only selectable / creatable when exactly
  // one brand is in the context filter. If 0 or ≥2 brands are selected,
  // ``selectedBrand`` is ``undefined`` so downstream consumers (version
  // fetch, CreateBudgetCube button, selector) can disable the
  // brand-dependent UI uniformly. A tenant-scoped budget has no brand at
  // all, which ``useScopedBudgetVersions`` resolves.
  const brands = selectedContextOptions?.brand ?? [];
  const selectedBrand = brands.length === 1 ? brands[0] : undefined;

  const { data: budgetVersions } = useScopedBudgetVersions(selectedBrand);

  // Frozen state (only meaningful when producer is "budget")
  const selectedBudgetVersion = budgetVersions?.budget_versions?.find(
    (v) => v.cube_id === selectedVersionOptions.budget_version
  );

  // By cube id, not just the picked one: a freeze lands on the applied cube
  // without an Apply, so the grid's gate has to read this live. ``undefined``
  // means the payload no longer lists the cube, which only its caller can
  // answer for.
  const isCubeFrozen = useCallback(
    (cubeId: string | undefined): boolean | undefined => {
      if (producer !== "budget" || !cubeId) return false;
      const version = budgetVersions?.budget_versions?.find(
        (v) => v.cube_id === cubeId
      );
      return version ? (version.is_frozen ?? false) : undefined;
    },
    [producer, budgetVersions]
  );

  // No cast: ``SaveCubeKey`` is ``"user_version" | "budget_version"``
  // and both keys resolve to ``string | undefined`` on
  // ``ForecastVersionSelections``.
  const saveCubeId: string | undefined = selectedVersionOptions[saveCubeKey];
  const isFrozen = isCubeFrozen(saveCubeId) ?? false;

  // The save cube's week window for the grid's out-of-window gate. The
  // versions payload is where the window travels; a cube without one leaves
  // the gate off (the server still rejects).
  const saveCubeWindow = useMemo<CubeWindow | undefined>(() => {
    const candidates =
      saveCubeKey === "budget_version"
        ? budgetVersions?.budget_versions
        : versionsData?.user_versions;
    const version = candidates?.find((v) => v.cube_id === saveCubeId);
    if (version?.year_week_min == null || version.year_week_max == null) {
      return undefined;
    }
    return {
      yearWeekMin: version.year_week_min,
      yearWeekMax: version.year_week_max
    };
  }, [saveCubeKey, budgetVersions, versionsData, saveCubeId]);

  // Flat list of all selected cube identifiers. The server resolves each
  // id's role (user / budget / reference / DS) via ``resolve_producer``;
  // ``save_cube_id`` (sent alongside) marks the writable own cube so the
  // caller's other versions can ride along as read-only references.
  const cubeIds = useMemo(() => {
    const ids: string[] = [];
    if (selectedVersionOptions.user_version) {
      ids.push(selectedVersionOptions.user_version);
    }
    if (selectedVersionOptions.budget_version) {
      ids.push(selectedVersionOptions.budget_version);
    }
    if (selectedVersionOptions.reference_users?.length) {
      ids.push(...selectedVersionOptions.reference_users);
    }
    if (selectedVersionOptions.forecast_version) {
      ids.push(selectedVersionOptions.forecast_version);
    }
    return ids;
  }, [selectedVersionOptions]);

  // Map a grid row's ``producer_type`` label back to the cube identifier
  // that row came from. Used by the stacked chart hook, which needs a
  // single cube_id per request. Reference cubes carry a label instead of
  // a role: the caller's own versions their version label, other users'
  // cubes ``owner:version`` — both looked up in the versions payload.
  const resolveCubeId = useCallback(
    (producerType: string): string | null => {
      // Algorithmic rows are page state with no cube behind them. This is the
      // only row-to-cube path, so it is where ``algo:<model>`` must stop
      // before it could ever reach a ``cube_ids`` request.
      if (isAlgorithmicProducer(producerType)) return null;
      if (producerType === "user") {
        return selectedVersionOptions.user_version ?? null;
      }
      if (producerType === "budget") {
        return selectedVersionOptions.budget_version ?? null;
      }
      if (producerType === "ds") {
        return selectedVersionOptions.forecast_version ?? null;
      }
      const match =
        versionsData?.user_versions?.find((v) => v.label === producerType) ??
        versionsData?.other_user_versions?.find(
          (v) => v.label === producerType
        );
      return match?.cube_id ?? null;
    },
    [selectedVersionOptions, versionsData]
  );

  return {
    versionsData,
    budgetVersions,
    selectedBudgetVersion,
    isFrozen,
    isCubeFrozen,
    saveCubeId,
    saveCubeWindow,
    cubeIds,
    resolveCubeId,
    selectedBrand
  };
}
